'use client';

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Building2, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import type { TenantAccessDto } from '@/lib/generated-api';
import { useToast } from '@/components/ui/use-toast';
import { useTheme } from '@/lib/theme-context';
import { getBackgroundClasses, getTextClasses, getBorderClasses } from '@/lib/theme-utils';

interface SelectTenantsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectableTenants: TenantAccessDto[];
  selectedTenantIds: string[];
  currentTenantId: string | undefined;
  onConfirm: (selectedTenantIds: string[]) => Promise<void>;
  isLoading?: boolean;
}

export function SelectTenantsDialog({
  open,
  onOpenChange,
  selectableTenants,
  selectedTenantIds: initialSelectedTenantIds,
  currentTenantId,
  onConfirm,
  isLoading = false,
}: SelectTenantsDialogProps) {
  const { theme } = useTheme();
  const { toast } = useToast();
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>(initialSelectedTenantIds);
  const [saving, setSaving] = useState(false);

  // Reset selection when dialog opens or initial selection changes
  // Honor whatever the backend provides in session.selectedTenantIds
  useEffect(() => {
    if (open) {
      setSelectedTenantIds(initialSelectedTenantIds);
    }
  }, [open, initialSelectedTenantIds]);

  const handleToggleTenant = (tenantId: string) => {
    setSelectedTenantIds((prev) => {
      if (prev.includes(tenantId)) {
        // Prevent deselecting if it's the last remaining tenant
        if (prev.length === 1) {
          return prev; // Keep at least one tenant selected
        }
        return prev.filter((id) => id !== tenantId);
      }
      return [...prev, tenantId];
    });
  };

  const handleSelectAll = () => {
    setSelectedTenantIds(selectableTenants.map((t) => t.tenantId!).filter(Boolean));
  };

  const handleDeselectAll = () => {
    // Ensure at least the current tenant remains selected
    // If no current tenant, keep the first available tenant
    const fallbackTenantId = currentTenantId || selectableTenants[0]?.tenantId;
    if (fallbackTenantId) {
      setSelectedTenantIds([fallbackTenantId]);
    }
  };

  const handleConfirm = async () => {
    // Ensure at least one tenant is selected (backend requirement)
    let finalSelection = selectedTenantIds;
    
    if (finalSelection.length === 0) {
      // Fallback to current tenant or first available tenant
      const fallbackTenantId = currentTenantId || selectableTenants[0]?.tenantId;
      if (fallbackTenantId) {
        finalSelection = [fallbackTenantId];
        setSelectedTenantIds(finalSelection);
        toast({
          title: 'Selection adjusted',
          description: 'At least one tenant must be selected. Using current tenant as fallback.',
          variant: 'default',
        });
      } else {
        toast({
          title: 'Error',
          description: 'No tenants available for selection.',
          variant: 'destructive',
        });
        return;
      }
    }

    setSaving(true);
    try {
      await onConfirm(finalSelection);
      toast({
        title: 'Tenants selected',
        description: `Data will be aggregated from ${finalSelection.length} tenant(s).`,
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update tenant selection.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedTenantIds(initialSelectedTenantIds);
    onOpenChange(false);
  };

  if (selectableTenants.length <= 1) {
    return null; // Don't render if there's only one or no selectable tenants
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-2xl ${getBackgroundClasses(theme, 'default')} ${getBorderClasses(theme)}`}>
        <DialogHeader>
          <DialogTitle className={`text-xl font-bold ${getTextClasses(theme)} flex items-center gap-2`}>
            <Building2 className="w-5 h-5" />
            Select Tenants for Data Aggregation
          </DialogTitle>
          <DialogDescription className={getTextClasses(theme, 'muted')}>
            Choose which tenant data should be aggregated and visible in cross-tenant views.
            At least one tenant must be selected at all times.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Selection summary */}
          <div className={`flex items-center justify-between p-3 rounded-lg border ${
            selectedTenantIds.length === 0
              ? '/20 /50'
              : `${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)}`
          }`}>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${
                selectedTenantIds.length === 0 ? 'text-yellow-300' : getTextClasses(theme)
              }`}>
                {selectedTenantIds.length === 0 ? (
                  <>
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    No tenants selected
                  </>
                ) : (
                  `${selectedTenantIds.length} of ${selectableTenants.length} tenant(s) selected`
                )}
              </span>
              {selectedTenantIds.length === 0 && (
                <span className="text-xs /70">
                  (At least one tenant must be selected)
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSelectAll}
                className={`${getTextClasses(theme)} hover:${getTextClasses(theme)}`}
                disabled={selectedTenantIds.length === selectableTenants.length}
              >
                Select All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDeselectAll}
                className={`${getTextClasses(theme)} hover:${getTextClasses(theme)}`}
                disabled={selectedTenantIds.length <= 1}
                title={selectedTenantIds.length <= 1 ? 'At least one tenant must remain selected' : undefined}
              >
                Deselect All
              </Button>
            </div>
          </div>

          {/* Tenant list */}
          <div className={`max-h-96 overflow-y-auto space-y-2 border ${getBorderClasses(theme)} rounded-lg p-2`}>
            {selectableTenants.map((tenant) => {
              const isSelected = selectedTenantIds.includes(tenant.tenantId!);
              const isCurrent = tenant.tenantId === currentTenantId;

              return (
                <div
                  key={tenant.tenantId}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                    isSelected
                      ? `${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)}`
                      : `${getBackgroundClasses(theme, 'default')} ${getBorderClasses(theme)} hover:${getBackgroundClasses(theme, 'card')}`
                  } ${isCurrent ? 'ring-2 ring-cyan-500/50' : ''}`}
                  onClick={() => handleToggleTenant(tenant.tenantId!)}
                >
                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleToggleTenant(tenant.tenantId!)}
                      disabled={isSelected && selectedTenantIds.length === 1}
                      className="cursor-pointer disabled:cursor-not-allowed"
                      title={isSelected && selectedTenantIds.length === 1 ? 'At least one tenant must remain selected' : undefined}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Building2 className={`w-4 h-4 ${getTextClasses(theme, 'muted')} flex-shrink-0`} />
                      <span className={`text-sm font-medium ${getTextClasses(theme)} truncate`}>
                        {tenant.tenantName || tenant.tenantSlug || tenant.tenantId}
                      </span>
                      {isCurrent && (
                        <Badge 
                          className="text-xs"
                          style={{
                            backgroundColor: 'var(--theme-info)',
                            color: 'var(--theme-foreground)',
                            borderColor: 'var(--theme-info)'
                          }}
                        >
                          Current
                        </Badge>
                      )}
                      {tenant.isActive === false && (
                        <Badge
                          variant="outline"
                          className="text-xs /10  border-yellow-500/30" style={{ backgroundColor: 'var(--theme-warning)' }}
                        >
                          Inactive
                        </Badge>
                      )}
                    </div>
                    {tenant.tenantSlug && tenant.tenantSlug !== tenant.tenantName && (
                      <div className={`text-xs ${getTextClasses(theme, 'muted')} mt-0.5 truncate`}>
                        {tenant.tenantSlug}
                      </div>
                    )}
                  </div>
                  {isSelected && (
                    <CheckCircle className="w-4 h-4  flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between">
          <div className={`text-xs ${getTextClasses(theme, 'muted')}`}>
            Selected tenants control which data appears in cross-tenant views and reports.
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={saving || isLoading}
              className={`${getBorderClasses(theme)} ${getTextClasses(theme)}`}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={saving || isLoading || selectedTenantIds.length === 0}
              style={{
                backgroundColor: 'var(--theme-accent)',
                color: 'var(--theme-accent-foreground)'
              }}
            >
              {saving || isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Applying...
                </>
              ) : (
                'Apply Selection'
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

